import { useState, useEffect } from "react";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import axios from "axios";
import {
  Building2,
  Save,
  CheckCircle2,
  AlertCircle,
  Percent,
  Users,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  User,
  Settings,
  Rocket,
  Calculator,
  RefreshCw,
  UserPlus,
  Trash2,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminHolding() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [holding, setHolding] = useState(null);
  const [testAmount, setTestAmount] = useState(100000);
  const [feeCalculation, setFeeCalculation] = useState(null);
  
  // Invite Admin state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [holdingAdmins, setHoldingAdmins] = useState([]);
  
  // Form state
  const [profile, setProfile] = useState({
    company_name: "",
    org_number: "",
    address: "",
    postal_code: "",
    city: "",
    country: "Norway",
    email: "",
    phone: "",
    bank_account: "",
    bank_name: "",
    legal_representative: "",
    legal_rep_title: "Daglig leder",
    logo_url: ""
  });
  
  const [fees, setFees] = useState({
    platform_fee: 2.0,
    holding_margin: 5.0,
    andre_kostnader: 3.0,
    aga: 14.5,
    feriepenger: 10.8
  });

  useEffect(() => {
    fetchHolding();
    fetchHoldingAdmins();
  }, []);

  const fetchHolding = async () => {
    try {
      const token = localStorage.getItem("scaffoldx_token");
      const res = await axios.get(`${API}/api/holding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data && res.data.status !== "not_configured") {
        setHolding(res.data);
        if (res.data.profile) setProfile(res.data.profile);
        if (res.data.fees) setFees(res.data.fees);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchHoldingAdmins = async () => {
    try {
      const token = localStorage.getItem("scaffoldx_token");
      const res = await axios.get(`${API}/api/holding/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHoldingAdmins(res.data || []);
    } catch (err) {
      console.error("Failed to fetch holding admins:", err);
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail || !inviteEmail.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    setInviting(true);
    try {
      const token = localStorage.getItem("scaffoldx_token");
      await axios.post(`${API}/api/holding/admins/invite`, 
        { email: inviteEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast({
        title: "Success!",
        description: `Admin invitation sent to ${inviteEmail}`
      });
      
      setInviteEmail("");
      setInviteDialogOpen(false);
      fetchHoldingAdmins();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to invite admin",
        variant: "destructive"
      });
    } finally {
      setInviting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("scaffoldx_token");
      
      if (holding) {
        // Update existing
        await axios.put(`${API}/api/holding`, {
          profile,
          fees
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new
        await axios.post(`${API}/api/holding`, {
          profile,
          fees
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast({
        title: "Saved!",
        description: "Holding company settings saved successfully"
      });
      
      fetchHolding();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to save",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    try {
      const token = localStorage.getItem("scaffoldx_token");
      await axios.post(`${API}/api/holding/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Activated!",
        description: "Holding company is now active"
      });
      
      fetchHolding();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to activate",
        variant: "destructive"
      });
    }
  };

  const calculateFees = async () => {
    try {
      const token = localStorage.getItem("scaffoldx_token");
      const res = await axios.get(`${API}/api/holding/fees/calculate?gross_amount=${testAmount}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeCalculation(res.data);
    } catch (err) {
      // Use local calculation with CORRECT formula
      // From gross amount, deduct platform fees first
      const platform_fee = testAmount * (fees.platform_fee / 100);
      const holding_margin_fee = testAmount * (fees.holding_margin / 100);
      const andre_kostnader = testAmount * (fees.andre_kostnader / 100);
      const labor_budget = testAmount - platform_fee - holding_margin_fee - andre_kostnader;
      
      // Team salary is calculated by dividing labor budget by (1 + AGA + Feriepenger)
      // This ensures AGA and Feriepenger are DEDUCTED from labor budget, not added on top
      const aga_rate = fees.aga / 100;
      const feriepenger_rate = fees.feriepenger / 100;
      const team_salary = labor_budget / (1 + aga_rate + feriepenger_rate);
      
      // AGA and Feriepenger are calculated on team salary
      const aga = team_salary * aga_rate;
      const feriepenger = team_salary * feriepenger_rate;
      
      // Verify: team_salary + aga + feriepenger should equal labor_budget
      const total_labor_cost = team_salary + aga + feriepenger;
      
      setFeeCalculation({
        gross_amount: testAmount,
        labor_budget: labor_budget.toFixed(2),
        deductions: {
          platform_fee: platform_fee.toFixed(2),
          holding_margin: holding_margin_fee.toFixed(2),
          andre_kostnader: andre_kostnader.toFixed(2),
          total_deductions: (platform_fee + holding_margin_fee + andre_kostnader).toFixed(2)
        },
        team_calculation: {
          team_salary: team_salary.toFixed(2),
          aga: aga.toFixed(2),
          feriepenger: feriepenger.toFixed(2),
          total_labor_cost: total_labor_cost.toFixed(2)
        },
        net_to_team: team_salary.toFixed(2),
        total_holding_cost: (platform_fee + holding_margin_fee + andre_kostnader + total_labor_cost).toFixed(2),
        holding_profit: holding_margin_fee.toFixed(2)
      });
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Holding Company">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Holding Company">
      <div className="space-y-6" data-testid="admin-holding">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white font-['Unbounded']">
              🏢 Holding Company Setup
            </h1>
            <p className="text-[#94A3B8]">Configure the holding company that employs all workers</p>
          </div>
          <div className="flex items-center gap-3">
            {holding?.status === "active" ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Active
              </Badge>
            ) : holding?.status === "pending_setup" ? (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 px-4 py-2">
                <AlertCircle className="w-4 h-4 mr-2" />
                Pending Setup
              </Badge>
            ) : (
              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 px-4 py-2">
                Not Configured
              </Badge>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Company Profile */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Building2 className="w-5 h-5 text-[#23F7DD]" />
                  Company Profile
                </CardTitle>
                <CardDescription>Legal company information for contracts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Company Name *</Label>
                    <Input
                      value={profile.company_name}
                      onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                      placeholder="Holding Company AS"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Org. Number *</Label>
                    <Input
                      value={profile.org_number}
                      onChange={(e) => setProfile({...profile, org_number: e.target.value})}
                      placeholder="XXX XXX XXX"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Address *</Label>
                  <Input
                    value={profile.address}
                    onChange={(e) => setProfile({...profile, address: e.target.value})}
                    placeholder="Storgata 1"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Postal Code</Label>
                    <Input
                      value={profile.postal_code}
                      onChange={(e) => setProfile({...profile, postal_code: e.target.value})}
                      placeholder="0001"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">City</Label>
                    <Input
                      value={profile.city}
                      onChange={(e) => setProfile({...profile, city: e.target.value})}
                      placeholder="Oslo"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Country</Label>
                    <Input
                      value={profile.country}
                      onChange={(e) => setProfile({...profile, country: e.target.value})}
                      placeholder="Norway"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email *
                    </Label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({...profile, email: e.target.value})}
                      placeholder="post@holding.no"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone
                    </Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      placeholder="+47 XXX XX XXX"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Bank Account
                    </Label>
                    <Input
                      value={profile.bank_account}
                      onChange={(e) => setProfile({...profile, bank_account: e.target.value})}
                      placeholder="XXXX XX XXXXX"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Bank Name</Label>
                    <Input
                      value={profile.bank_name}
                      onChange={(e) => setProfile({...profile, bank_name: e.target.value})}
                      placeholder="DNB"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4" /> Legal Representative *
                    </Label>
                    <Input
                      value={profile.legal_representative}
                      onChange={(e) => setProfile({...profile, legal_representative: e.target.value})}
                      placeholder="Full Name"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Title</Label>
                    <Input
                      value={profile.legal_rep_title}
                      onChange={(e) => setProfile({...profile, legal_rep_title: e.target.value})}
                      placeholder="Daglig leder"
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fees Configuration */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Percent className="w-5 h-5 text-[#23F7DD]" />
                  Fee Configuration
                </CardTitle>
                <CardDescription>Configure deduction percentages</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30">
                    <Label className="text-[#23F7DD]">Platform Fee (→ AllStillas)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={fees.platform_fee}
                        onChange={(e) => setFees({...fees, platform_fee: parseFloat(e.target.value) || 0})}
                        className="bg-white/5 border-white/10 w-24"
                        data-testid="platform-fee-input"
                      />
                      <span className="text-white">%</span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">Revenue for AllStillas Platform</p>
                  </div>
                  <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <Label className="text-orange-400">Holding Margin (→ Holding Company)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={fees.holding_margin}
                        onChange={(e) => setFees({...fees, holding_margin: parseFloat(e.target.value) || 0})}
                        className="bg-white/5 border-white/10 w-24"
                        data-testid="holding-margin-input"
                      />
                      <span className="text-white">%</span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">Operating margin for Holding (hidden from client)</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-1 gap-4">
                  <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <Label className="text-purple-400">Andre Kostnader (Pension, Insurance etc.)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={fees.andre_kostnader}
                        onChange={(e) => setFees({...fees, andre_kostnader: parseFloat(e.target.value) || 0})}
                        className="bg-white/5 border-white/10 w-24"
                        data-testid="andre-kostnader-input"
                      />
                      <span className="text-white">%</span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">Employer social costs (pension, insurance)</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <Label className="text-blue-400">AGA (Arbeidsgiveravgift)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={fees.aga}
                        onChange={(e) => setFees({...fees, aga: parseFloat(e.target.value) || 0})}
                        className="bg-white/5 border-white/10 w-24"
                      />
                      <span className="text-white">%</span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">Paid by Holding over gross</p>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <Label className="text-green-400">Feriepenger (Holiday Pay)</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={fees.feriepenger}
                        onChange={(e) => setFees({...fees, feriepenger: parseFloat(e.target.value) || 0})}
                        className="bg-white/5 border-white/10 w-24"
                      />
                      <span className="text-white">%</span>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">Paid in June</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex gap-4">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Configuration"}
              </Button>
              
              {holding && holding.status === "pending_setup" && (
                <Button 
                  onClick={handleActivate}
                  className="bg-green-500 text-white hover:bg-green-600 font-bold"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Activate Holding
                </Button>
              )}
            </div>
          </div>

          {/* Fee Calculator */}
          <div className="space-y-6">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Calculator className="w-5 h-5 text-[#23F7DD]" />
                  Fee Calculator
                </CardTitle>
                <CardDescription>Test fee calculations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Gross Amount (NOK)</Label>
                  <Input
                    type="number"
                    value={testAmount}
                    onChange={(e) => setTestAmount(parseInt(e.target.value) || 0)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <Button 
                  onClick={calculateFees}
                  className="w-full bg-white/10 hover:bg-white/20"
                >
                  Calculate
                </Button>
                
                {feeCalculation && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <div className="text-sm">
                      <p className="text-[#94A3B8]">Client Budget (Gross)</p>
                      <p className="text-white font-bold">{parseFloat(feeCalculation.gross_amount).toLocaleString()} NOK</p>
                    </div>
                    
                    <div className="p-3 bg-purple-500/10 rounded-lg">
                      <p className="text-purple-400 font-semibold mb-2">Total Deductions:</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Platform fee ({fees.platform_fee}%)</span>
                          <span className="text-[#23F7DD]">{parseFloat(feeCalculation.deductions.platform_fee).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Holding margin ({fees.holding_margin}%)</span>
                          <span className="text-orange-400">{parseFloat(feeCalculation.deductions?.holding_margin || feeCalculation.holding_profit || 0).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Andre kostnader ({fees.andre_kostnader}%)</span>
                          <span className="text-purple-400">{parseFloat(feeCalculation.deductions.andre_kostnader).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between font-bold border-t border-white/10 pt-1">
                          <span className="text-white">Total Deductions</span>
                          <span className="text-purple-400">{parseFloat(feeCalculation.deductions.total_deductions).toLocaleString()} NOK</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-500/10 rounded-lg">
                      <p className="text-blue-400 font-semibold mb-2">Labor Budget (after deductions):</p>
                      <p className="text-blue-300 font-bold text-lg">{parseFloat(feeCalculation.labor_budget || (feeCalculation.gross_amount - feeCalculation.deductions.total_deductions)).toLocaleString()} NOK</p>
                      <p className="text-xs text-[#94A3B8] mt-1">This covers: Team Salary + AGA + Feriepenger</p>
                    </div>
                    
                    <div className="p-3 bg-cyan-500/10 rounded-lg">
                      <p className="text-cyan-400 font-semibold mb-2">Breakdown from Labor Budget:</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Team Salary (Net to workers)</span>
                          <span className="text-green-400 font-bold">{parseFloat(feeCalculation.net_to_team).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">+ AGA ({fees.aga}% of salary)</span>
                          <span className="text-cyan-400">{parseFloat(feeCalculation.employer_costs?.aga || feeCalculation.team_calculation?.aga || 0).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">+ Feriepenger ({fees.feriepenger}% of salary)</span>
                          <span className="text-cyan-400">{parseFloat(feeCalculation.employer_costs?.feriepenger || feeCalculation.team_calculation?.feriepenger || 0).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between font-bold border-t border-white/10 pt-1">
                          <span className="text-white">= Total Labor Cost</span>
                          <span className="text-cyan-400">{parseFloat(feeCalculation.labor_budget || 0).toLocaleString()} NOK</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-green-400 font-semibold">💰 Team Receives</span>
                          <p className="text-xs text-[#94A3B8]">Gross salary to team</p>
                        </div>
                        <span className="text-green-400 font-bold text-xl">{parseFloat(feeCalculation.net_to_team).toLocaleString()} NOK</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-500/10 rounded-lg">
                      <p className="text-[#94A3B8] text-xs mb-2">Profit Distribution:</p>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Client pays:</span>
                          <span className="text-white">{parseFloat(feeCalculation.gross_amount).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#23F7DD]">→ AllStillas Platform:</span>
                          <span className="text-[#23F7DD]">{parseFloat(feeCalculation.deductions.platform_fee).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-400">→ Holding Company:</span>
                          <span className="text-orange-400">{parseFloat(feeCalculation.deductions?.holding_margin || feeCalculation.holding_profit || 0).toLocaleString()} NOK</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">→ Labor costs:</span>
                          <span className="text-cyan-400">{parseFloat(feeCalculation.labor_budget || 0).toLocaleString()} NOK</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-xs text-[#64748B] italic">
                      * Skatt is calculated individually per worker
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Admins */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="w-5 h-5 text-[#23F7DD]" />
                  Holding Admins
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-[#94A3B8]">
                  Holding admins can approve projects, manage workers, and sign employment contracts.
                </p>
                
                {/* List of current admins */}
                {holdingAdmins.length > 0 && (
                  <div className="space-y-2">
                    {holdingAdmins.map((admin) => (
                      <div 
                        key={admin.user_id || admin.email}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{admin.name || "Holding Admin"}</p>
                            <p className="text-xs text-[#94A3B8]">{admin.email}</p>
                          </div>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          Active
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {holdingAdmins.length === 0 && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      No holding admins configured yet. Invite an admin to manage holding operations.
                    </p>
                  </div>
                )}
                
                <Button 
                  className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                  onClick={() => setInviteDialogOpen(true)}
                  data-testid="invite-admin-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Admin
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Invite Admin Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#23F7DD]" />
              Invite Holding Admin
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Enter the email of the person you want to invite as a Holding Admin.
              If they already have an account, their role will be upgraded.
              If not, they will receive an invitation email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-white">Email Address</Label>
              <Input
                type="email"
                placeholder="admin@company.no"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="invite-email-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setInviteDialogOpen(false)}
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteAdmin}
                disabled={inviting || !inviteEmail}
                className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                data-testid="send-invite-btn"
              >
                {inviting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Inviting...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send Invite</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
